import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const initiatives = [
  { name: 'Recycling Bins in the Cafeteria', desc: 'Setting up and maintaining recycling stations so students can build green habits every day.' },
  { name: 'Teaching Younger Students', desc: 'Green Team members visit classrooms to teach elementary students how to sort and recycle properly.' },
  { name: 'Can Collection Drives', desc: 'Organizing school-wide collections to reduce waste and support community sustainability goals.' },
  { name: 'Earth Day Celebrations', desc: 'Leading creative events and activities that bring the whole school together around Earth Day.' },
  { name: 'Recycled Christmas Ornaments', desc: 'Crafting holiday decorations from repurposed materials — creativity meets sustainability.' },
  { name: 'School Sustainability Plan', desc: "Helping implement and carry out our school's long-term plan for a greener, healthier environment." },
];

const GreenTeamPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section
        className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
      >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <span className="text-sm font-semibold text-white tracking-tight">Student Life</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Green Team
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            Middle school eco-leaders on a mission to make sustainability a way of life at PS 96.
          </p>
        </div>
      </section>

      {/* Section 1 — image left, text right */}
      <section className="flex flex-col lg:flex-row min-h-[460px]">
        <div className="lg:w-1/2 h-64 lg:h-auto overflow-hidden">
          <img
            src="/assets/images/greenteam/IMG_3230.jpeg"
            alt="PS 96 Green Team students"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="lg:w-1/2 bg-[#00213a] flex items-center px-10 py-14 lg:px-16">
          <div className="max-w-lg">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#fdbb2e] mb-3">About the Club</p>
            <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
              Eco-Leaders Making a Real Difference.
            </h2>
            <p className="text-white/80 leading-relaxed text-lg">
              Led by <strong className="text-white">Mrs. Wysong</strong> and a passionate group of middle school students, our Green Team is on a mission to make sustainability a way of life at our school. More than just a club, the Green Team empowers students to take action — turning big ideas into real-world projects that help our planet.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2 — text left, image right */}
      <section className="flex flex-col-reverse lg:flex-row min-h-[460px]">
        <div className="lg:w-1/2 bg-white flex items-center px-10 py-14 lg:px-16">
          <div className="max-w-lg">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#00213a] mb-3">Our Mission</p>
            <h2 className="text-3xl lg:text-4xl font-semibold text-gray-900 mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
              Caring for the Environment Is Second Nature.
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg">
              By fostering hands-on learning, creativity, and teamwork, our Green Team is helping to build a school culture where environmental responsibility is woven into everyday life. These eco-leaders work hard to bring our school's sustainability plan to life — one project at a time.
            </p>
          </div>
        </div>
        <div className="lg:w-1/2 h-64 lg:h-auto overflow-hidden">
          <img
            src="/assets/images/greenteam/IMG_3269__1_.jpeg"
            alt="Green Team project at PS 96"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Photo gallery — photos keep their own shape (no cropping), so faces are never cut off */}
      <section className="bg-gray-50 py-20" aria-labelledby="green-team-gallery-heading">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 id="green-team-gallery-heading" className="text-4xl font-semibold text-gray-900 tracking-tight mb-10 text-center" style={{ letterSpacing: '-0.02em' }}>
            Green Team in Action
          </h2>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
            {[
              { src: '/assets/images/greenteam/green-team-painted-rocks.jpg', alt: 'Painted rocks spelling out PS 96 Green Team, with messages like Stop Climate Change' },
              { src: '/assets/images/greenteam/7b095a0e-eb67-4476-8d39-d2d3a798e703.jpg', alt: 'Two PS 96 Green Team students sit with clipboards, taking notes during a workshop' },
              { src: '/assets/images/greenteam/green-team-building-project.jpg', alt: 'Green Team students in school sweatshirts work together on a hands-on building project at a classroom table' },
              { src: '/assets/images/greenteam/11f47f2a-ba56-4523-a338-36cd36de1f2a.jpg', alt: 'Green Team students sort colorful fabric scraps at a table during a textile workshop' },
              { src: '/assets/images/greenteam/green-team-painting-rocks.jpg', alt: 'A Green Team student paints a rock with markers and paint pens at an art table' },
            ].map((photo) => (
              <img
                key={photo.src}
                src={photo.src}
                alt={photo.alt}
                loading="lazy"
                className="mb-5 w-full h-auto rounded-2xl shadow-sm break-inside-avoid"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Initiatives Grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/5 rounded-lg border border-[#fdbb2e]/30 mb-4">
            <span className="text-sm font-semibold text-[#00213a]">What We Do</span>
          </div>
          <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Green Team Initiatives
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {initiatives.map((item) => (
            <div key={item.name} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md hover:border-[#fdbb2e]/30 transition-all duration-200">
              <div className="w-8 h-8 rounded-lg bg-[#fdbb2e]/10 flex items-center justify-center mb-4">
                <svg className="w-4 h-4 text-[#00213a]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">{item.name}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#00213a] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Join the Green Team
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Are you a middle school student passionate about the environment? Talk to Mrs. Wysong about joining the Green Team.
          </p>
          <a
            href="/contact-us"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#fdbb2e] text-[#00213a] font-semibold rounded-xl hover:bg-yellow-200 transition-colors duration-200 shadow-sm"
          >
            Contact Us <ArrowRightIcon />
          </a>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default GreenTeamPage;
